const SENSORS_ICONS = {
  "default": "microchip-solid",
  "a": "temperature-high-solid",
  "b": "humidity_new3",
  "c": "pressure",
  "d": "sun-solid",
  "e": "air-sensor",
  "f": "sun-regular",
  "g": "leaf-solid",
  "h": "temperature-high-solid",
  "i": "soil-vwc",
  "l": "soil-ec_new",
  "m": "co2_new",
  "n": "balance_new",
  "o": "liquid-level",
  "za": "dew-point_new",
  "zb": "vpd_new",
  "zc": "du_new",
  "r": "soil-ec_new",
  "s": "soil-ec_new",
  "t": "humidity_new3",
  "u": "soil-vwc",
  "v": "leaf-solid",
  "p": "soil-ph"
};

// EPWA-984/982: convenzioni codici sensore substrato usate dalle Home Calculations.
// Fonte: famiglia 'h' = temperatura (SENSORS_ICONS); 'i' = soil-vwc (SENSORS_ICONS + i18n
// SENSORS.i*).
//
// La specifica aggiornata di EPWA-982/EPWA-984 chiede l'intera famiglia 'h', non piu' il
// sottoinsieme ['ha','hb','hf','hg']. Conseguenza da tenere presente: rientrano anche
// hc/hd/he, elencati fra i water sensor in environment.sensors.waterSensor, quindi le card
// Solarizzazione e Germinabilita' possono ora proporre sensori di temperatura acqua.
export const SUBSTRATE_TEMPERATURE_PREFIXES = ['h'];
export const SOIL_VWC_PREFIXES = ['i'];

// EPWA-982: la Germinabilità si calcola con un sensore di umidità scelto dall'utente fra i
// VWC (i*, %) e le bilance (n*, kg). Il backend accetta i primi solo in `germinationHours.vwc`
// e le seconde solo in `germinationHoursByWeight.weight`.
export const SOIL_WEIGHT_PREFIXES = ['n'];
export const GERMINATION_MOISTURE_PREFIXES = [...SOIL_VWC_PREFIXES, ...SOIL_WEIGHT_PREFIXES];

export function isGerminationWeightSensor(type: string | null | undefined): boolean {
  return !!type && SOIL_WEIGHT_PREFIXES.some((p) => type.startsWith(p));
}

// Sensore di umidità di un box Germinabilità: quello salvato se esiste ancora sul nodo,
// altrimenti il VWC con lo stesso suffisso della temperatura (ha0 -> ia0), cioè l'abbinamento
// in uso prima della select, così le impostazioni già salvate continuano a dare lo stesso
// valore. Senza nessuno dei due il box resta da configurare.
export function resolveGerminationMoistureSensor(
  tempType: string,
  saved: string | null | undefined,
  sensorTypes: string[],
): string | null {
  if (saved && sensorTypes.includes(saved)) {
    return saved;
  }
  const suffix = tempType.slice(1);
  return (
    sensorTypes.find((t) => SOIL_VWC_PREFIXES.some((p) => t.startsWith(p)) && t.slice(1) === suffix) ??
    null
  );
}

export function getSensorIcon(type: string): string {
  if (type.startsWith('z')) {
    return SENSORS_ICONS[type.substr(0, 2)] !== undefined ? SENSORS_ICONS[type.substr(0, 2)] : SENSORS_ICONS['default'];
  } else {
    return SENSORS_ICONS[type.substr(0, 1)] !== undefined ? SENSORS_ICONS[type.substr(0, 1)] : SENSORS_ICONS['default'];
  }
}

/**
 * Restituisce il suffisso numerico ("1", "2", …) da accodare alla label tradotta
 * quando esistono più sensori con lo STESSO display label; stringa vuota se è unico.
 *
 * La numerazione raggruppa per LABEL TRADOTTO (parità con Flutter
 * `sensor_label_formatter.dart::formatSensorLabels`): codici diversi che traducono
 * lo stesso label (es. `oa`/`ob` → "Liquid Level") vengono contati insieme → 1,2,3,4.
 *
 * @param targetType   codice sensore di cui calcolare il suffisso (es. "oa0").
 * @param sensors      tutti i codici sensore visibili nel contesto.
 * @param sensorLabels mappa codice(2 lettere)→label tradotto, tipicamente
 *                     `translateService.instant("SENSORS")`. Se assente, fallback al
 *                     raggruppamento per codice `type.substr(0,2)` (comportamento storico).
 */
export function formatSensorLabel(
  targetType: string,
  sensors: string[],
  sensorLabels?: { [code: string]: string }
): string {
  if (!sensors || sensors.length === 0) {
    return "";
  }

  const groupKey = (type: string): string => {
    const code = type.substr(0, 2);
    const label = sensorLabels ? sensorLabels[code] : undefined;
    return label != null && label !== "" ? label : code;
  };

  // 1. preserva l'ordine di input = ordine di display delle card (parità Flutter:
  //    `groupSensorsByType` non riordina, quindi la numerazione deve seguire la lista).
  const ordered = [...sensors];

  // 2. conta le occorrenze per chiave di gruppo (label tradotto)
  const counts: { [key: string]: number } = {};
  ordered.forEach(type => {
    const key = groupKey(type);
    counts[key] = (counts[key] ?? 0) + 1;
  });

  // 3. contatore progressivo sui soli duplicati; label unico → suffisso vuoto
  const running: { [key: string]: number } = {};
  const suffixByType: { [type: string]: string } = {};
  ordered.forEach(type => {
    const key = groupKey(type);
    if (counts[key] > 1) {
      running[key] = (running[key] ?? 0) + 1;
      suffixByType[type] = String(running[key]);
    } else {
      suffixByType[type] = "";
    }
  });

  return suffixByType[targetType] ?? "";
}

export function replaceNumberView(numberToReplace): string {
  if (numberToReplace != null) {
    return numberToReplace.toString().replace('.', ',').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  }
  return "--";
}

export function setDecimalToView(numberToConvert: number) {
  return replaceNumberView(numberToConvert);
}
